import type { Floorplan } from '@vibesites/schema';

/** Minimal floor-plan shape needed to derive bedroom counts. */
export type FloorplanBedsSource = Pick<Floorplan, 'floorplan_beds_min'> | {
  floorplan_beds_min?: string | null;
};

/**
 * Shared bedroom-options formatting for property_bedrooms_range (and related
 * copy). Lists available bedroom counts individually — never as a dashed
 * range — and strips redundant bed/bedroom wording.
 *
 * Examples:
 *   [2]           → "2 bedrooms"
 *   [1]           → "1 bedroom"
 *   [0]           → "Studio"
 *   [1, 2]         → "1 & 2 bedrooms"
 *   [1, 2, 3]     → "1, 2, & 3 bedrooms"
 *   [0, 1, 2, 3]  → "Studio, 1, 2, & 3 bedrooms"
 */

const BED_TERM = String.raw`bedrooms?|beds?|bd|br`;

/**
 * Parse a beds value ("0", "studio", "2", "2 bd") into a numeric count
 * (0 = studio).
 */
export function parseBedCount(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  let trimmed = String(raw).trim().toLowerCase();
  if (!trimmed) return null;
  // Allow compact forms like "2bd" / "2 br" alongside "2 bedrooms".
  trimmed = trimmed.replace(new RegExp(String.raw`\s*(?:${BED_TERM})\s*$`, 'i'), '').trim();
  if (!trimmed) return null;
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 0;
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num < 0) return null;
  return num;
}

/**
 * Collect unique bedroom counts from floor plans, sorted ascending.
 */
export function bedCountsFromFloorplans(
  floorplans: FloorplanBedsSource[] | null | undefined,
): number[] {
  if (!floorplans?.length) return [];
  const beds = new Set<number>();
  for (const plan of floorplans) {
    const count = parseBedCount(plan.floorplan_beds_min);
    if (count != null) beds.add(count);
  }
  return [...beds].sort((a, b) => a - b);
}

/**
 * Expand a raw bedrooms-range string ("2 - 2", "studio to 3", "2 bd - 3 bd")
 * into discrete bed counts. Equal endpoints collapse to one; unequal
 * endpoints expand as a contiguous inclusive range when both ends parse.
 */
export function bedCountsFromRange(raw: string | null | undefined): number[] {
  if (!raw) return [];
  const trimmed = String(raw).trim();
  if (!trimmed) return [];

  // First try a single value (including "2 bd" with no dash).
  const single = parseBedCount(trimmed);
  if (single != null && !/\s*(?:[-–—]|to)\s*/i.test(trimmed)) {
    return [single];
  }

  const parts = trimmed
    .split(/\s*(?:[-–—]|to)\s*/i)
    .map((p) => parseBedCount(p))
    .filter((n): n is number => n != null);

  if (!parts.length) return [];
  if (parts.length === 1) return parts;

  const min = Math.min(...parts);
  const max = Math.max(...parts);
  if (min === max) return [min];

  const beds: number[] = [];
  for (let i = min; i <= max; i++) beds.push(i);
  return beds;
}

/**
 * Format bedroom options as listed numbers (never a dashed range).
 */
export function formatBedroomOptionsList(beds: number[]): string | null {
  if (!beds.length) return null;

  if (beds.length === 1) {
    const only = beds[0];
    if (only === 0) return 'Studio';
    return only === 1 ? '1 bedroom' : `${only} bedrooms`;
  }

  const parts = beds.map((b) => (b === 0 ? 'Studio' : String(b)));
  const list =
    parts.length === 2
      ? `${parts[0]} & ${parts[1]}`
      : `${parts.slice(0, -1).join(', ')}, & ${parts[parts.length - 1]}`;
  const allStudios = beds.every((b) => b === 0);
  return allStudios ? list : `${list} bedrooms`;
}

/**
 * Prefer distinct bedroom types present on floor plans; fall back to expanding
 * the property_bedrooms_range scalar when plans are unavailable.
 */
export function resolveBedroomOptionsLabel(
  floorplans: FloorplanBedsSource[] | null | undefined,
  range: string | null | undefined,
): string | null {
  return (
    formatBedroomOptionsList(bedCountsFromFloorplans(floorplans)) ||
    formatBedroomOptionsList(bedCountsFromRange(range))
  );
}

/**
 * Escape text for safe insertion into resolved richtext HTML.
 * Mirrors @vibesites/runtime resolveRichText field-token escaping so
 * labels with "&" match the already-resolved markup (`&amp;`).
 */
export function escapeHtmlText(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Collapse duplicated bed phrases like "2 bd 2 bd" / "2 bedroom 2 bedroom"
 * down to a single occurrence before further normalization.
 */
function collapseDuplicateBedPhrases(html: string): string {
  if (!html) return html;
  return html.replace(
    new RegExp(
      String.raw`\b((?:studio|std|\d+)\s*(?:${BED_TERM})?)\s+(?:\1(?:\s+|$))+`,
      'gi',
    ),
    '$1 ',
  );
}

/**
 * Strip bedroom-related terms (bed, beds, bd, bedroom, bedrooms, br) from
 * copy when the bedroom count is already shown. Keeps "bedroom(s)" that
 * immediately follow a digit so formatted labels like "1, 2, & 3 bedrooms"
 * stay intact.
 */
export function stripRedundantBedroomTerms(html: string): string {
  if (!html) return html;
  return html
    .replace(new RegExp(String.raw`(?<!\d\s*)\b(?:${BED_TERM})\b`, 'gi'), '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1');
}

/**
 * Normalize bedroom copy in plain text or resolved richtext HTML:
 * - Collapse duplicated phrases ("2 bd 2 bd")
 * - Expand dashed ranges ("1-3", "1 – 3 bedrooms") into the listed label
 * - When no range/token is present, swap the first bedroom term for the label
 * - Strip any remaining redundant bed/bedroom wording
 *
 * Uses the HTML-escaped label for presence checks and insertions because
 * resolveRichText escapes "&" to "&amp;" — otherwise "1 & 2" looks missing
 * and gets injected a second time ("1 & 2 1 & 2").
 */
export function normalizeBedroomDescription(
  html: string,
  label: string | null,
): string {
  if (!html) return html;

  let out = collapseDuplicateBedPhrases(html);

  if (!label) return stripRedundantBedroomTerms(out);

  const labelHtml = escapeHtmlText(label);
  const alreadyHasLabel = out.includes(label) || out.includes(labelHtml);

  out = out.replace(
    new RegExp(
      String.raw`\b(?:studio|std|\d+)\s*(?:[-–—]|to)\s*(?:studio|std|\d+)(?:\s*(?:${BED_TERM}))?\b`,
      'gi',
    ),
    labelHtml,
  );

  // Also expand single values that still carry a bed term ("2 bd", "3 bedroom")
  // when they don't already match the canonical label.
  if (!out.includes(label) && !out.includes(labelHtml)) {
    out = out.replace(
      new RegExp(String.raw`\b(?:studio|std|\d+)\s*(?:${BED_TERM})\b`, 'gi'),
      labelHtml,
    );
  }

  if (
    !alreadyHasLabel &&
    !out.includes(label) &&
    !out.includes(labelHtml)
  ) {
    const swapped = out.replace(
      new RegExp(String.raw`\b(?:${BED_TERM})\b`, 'i'),
      labelHtml,
    );
    if (swapped !== out) out = swapped;
  }

  return stripRedundantBedroomTerms(out);
}
